import { createTheme } from '@mui/material/styles';
import type { SxProps, Theme, PaletteMode } from '@mui/material/styles';

const WAF_YELLOW = '#FFCC00';
const WAF_YELLOW_SOFT = '#FFF3B0';
const WAF_YELLOW_DARK = '#FFCC00';

const LIGHT = {
  bg: '#F4F5F7',
  paper: '#FFFFFF',
  textPrimary: '#111827',
  textSecondary: '#374151',
  textDisabled: '#9CA3AF',
  divider: '#E5E7EB',
  inputBg: '#FFFFFF',
  rowHover: 'rgba(255, 204, 0, 0.10)',
  rowSelected: 'rgba(255, 204, 0, 0.18)',
  footerBg: WAF_YELLOW,
  headerBg: WAF_YELLOW,
} as const;

const DARK = {
  bg: '#0F1115',
  paper: '#171A1F',
  textPrimary: '#EDEEF0',
  textSecondary: '#B8BFC9',
  textDisabled: '#6B7280',
  divider: '#2A2E36',
  inputBg: '#1F232A',
  rowHover: 'rgba(255, 204, 0, 0.12)',
  rowSelected: 'rgba(255, 204, 0, 0.22)',
  // Use a slightly deeper/amber yellow on dark so it doesn't feel like a
  // torch beam. Hue-preserved at ~75% brightness.
  footerBg: '#BF9A00',
  headerBg: '#BF9A00',
} as const;

export const makeTheme = (mode: PaletteMode) => {
  const p = mode === 'dark' ? DARK : LIGHT;
  return createTheme({
    palette: {
      mode,
      primary: {
        main: WAF_YELLOW,
        light: WAF_YELLOW_SOFT,
        dark: '#C9A700',
        contrastText: '#111827',
      },
      background: {
        default: p.bg,
        paper: p.paper,
      },
      text: {
        primary: p.textPrimary,
        secondary: p.textSecondary,
        disabled: p.textDisabled,
      },
      divider: p.divider,
    },
    typography: {
      fontFamily:
        '"Inter","Roboto","Helvetica Neue","Segoe UI","Arial",sans-serif',
      h1: { fontWeight: 600, fontSize: '2rem', letterSpacing: '-0.01em' },
      h2: { fontWeight: 600, fontSize: '1.75rem', letterSpacing: '-0.01em' },
      h3: {
        fontWeight: 600,
        fontSize: '1.5rem',
        letterSpacing: '-0.01em',
        // Slightly smaller on phones so page titles don't crowd the toolbar /
        // wrap awkwardly at ~360px. Desktop is unchanged (md+ keeps 1.5rem).
        '@media (max-width:600px)': { fontSize: '1.3rem' },
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.25rem',
        '@media (max-width:600px)': { fontSize: '1.1rem' },
      },
      h5: { fontWeight: 600, fontSize: '1.1rem' },
      h6: { fontWeight: 600, fontSize: '1rem' },
      body2: { color: p.textSecondary },
    },
    shape: {
      borderRadius: 10,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: p.bg },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: `1px solid ${p.divider}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 500, borderRadius: 8 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500 },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'default' },
        styleOverrides: {
          root: {
            backgroundColor: p.paper,
            borderBottom: `1px solid ${p.divider}`,
            color: p.textPrimary,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${p.divider}` },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { backgroundColor: p.inputBg },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: p.inputBg,
            '&:hover': { backgroundColor: p.inputBg },
            '&.Mui-focused': { backgroundColor: p.inputBg },
          },
        },
      },
    },
  });
};

// Shared DataGrid styling — theme-aware so it reacts to light/dark mode.
export const dataGridSx: SxProps<Theme> = (theme) => {
  const isDark = theme.palette.mode === 'dark';
  const headerBg = isDark ? DARK.headerBg : LIGHT.headerBg;
  const footerBg = isDark ? DARK.footerBg : LIGHT.footerBg;
  const rowHover = isDark ? DARK.rowHover : LIGHT.rowHover;
  const rowSelected = isDark ? DARK.rowSelected : LIGHT.rowSelected;
  const headerText = '#111827'; // brand yellow header always reads dark text
  return {
    border: 0,
    fontSize: 14,
    color: theme.palette.text.primary,
    // Yellow only on the actual header row. The filler (which covers the
    // empty body area when there are no rows) stays on the paper colour so
    // an empty grid doesn't drown the page in yellow.
    '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader': {
      backgroundColor: headerBg,
    },
    // Everything that paints the empty body must match the paper colour so
    // a no-rows grid doesn't render the "horrible grey" default surface.
    // x-data-grid v7 DOM: the scroller is the body surface; the filler covers
    // the leftover area beneath short row sets; the overlay/overlayWrapper
    // host the "No rows" message. Cover all of them.
    '& .MuiDataGrid-virtualScroller, & .MuiDataGrid-filler, & .MuiDataGrid-overlayWrapper, & .MuiDataGrid-overlayWrapperInner': {
      backgroundColor: theme.palette.background.paper,
    },
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 700,
      color: headerText,
      letterSpacing: '0.01em',
    },
    '& .MuiDataGrid-columnSeparator': { display: 'none' },
    '& .MuiDataGrid-cell': {
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      alignItems: 'center',
      userSelect: 'none',
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
      transition: 'background-color 120ms ease',
    },
    '& .MuiDataGrid-row:hover': {
      backgroundColor: rowHover,
    },
    '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover': {
      backgroundColor: rowSelected,
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-footerContainer': {
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: footerBg,
    },
    '& .MuiTablePagination-root, & .MuiTablePagination-root .MuiTablePagination-selectLabel, & .MuiTablePagination-root .MuiTablePagination-displayedRows': {
      color: headerText,
      fontWeight: 500,
    },
    // The overlay hosts "No rows" / loading. Keep it translucent over paper
    // (so a loading spinner reads as a veil) but the wrapper underneath is
    // already paper, so no grey shows through.
    '& .MuiDataGrid-overlay': {
      backgroundColor: isDark ? 'rgba(23,26,31,0.7)' : 'rgba(255,255,255,0.7)',
    },
  };
};

// Shared MUI <Table> head styling — for the handful of pages that use a raw
// <Table>/<TableHead> instead of a DataGrid (e.g. Users, which needs a custom
// checkbox column). Gives the header row the brand yellow with dark bold text
// so it matches the DataGrid house style. Reuses the same LIGHT/DARK tokens.
export const wafTableHeadSx: SxProps<Theme> = (theme) => {
  const isDark = theme.palette.mode === 'dark';
  const headerBg = isDark ? DARK.headerBg : LIGHT.headerBg;
  const headerText = '#111827'; // brand yellow header always reads dark text
  return {
    '& .MuiTableCell-head': {
      backgroundColor: headerBg,
      color: headerText,
      fontWeight: 700,
      letterSpacing: '0.01em',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  };
};

// Back-compat: default export is the light theme for any stale imports.
export const theme = makeTheme('light');

export { WAF_YELLOW, WAF_YELLOW_SOFT, WAF_YELLOW_DARK };
